import * as React from "react";
import { Info } from "lucide-react";

import {
  Stage,
  StageChartHeading,
  StageFacts,
  StageHeadline,
  StageLead,
  StageMeta,
} from "@/components/site/sections/six-pillars-preview/stage";
import { sixPillarsPulsePreview } from "@/lib/six-pillars-preview";
import {
  formatPercent,
  formatShortDate,
  pulseOkPercent,
  pulseShareParts,
  pulseSummary,
  type PulseStageCycle,
} from "@/lib/six-pillars-stage";
import { cn } from "@/lib/utils";

// The Pulse lead page's stage (cooperly_app pages/pulse/lead/data.tsx and
// features/pulse/pulse-weekly-bars.tsx): the period average on the left and one
// share-of-OK bar per weekly cycle on the right.

/** Height of a bar: 100% of the people who answered. */
const PLOT_HEIGHT = 132;
/** Surface gap between the OK and Not OK parts. */
const PART_GAP = 2;

const LEGEND = [
  { label: "OK", className: "bg-[#28A68E]" },
  { label: "Not OK", className: "bg-[rgba(229,102,74,0.45)]" },
];

export function PulseStage() {
  const headingId = React.useId();
  const chartTitleId = React.useId();
  const { cycles, schedule } = sixPillarsPulsePreview;
  const summary = pulseSummary(cycles);
  const latest = summary.latest;

  return (
    <Stage
      labelledBy={headingId}
      contentClassName="flex-[1_1_360px]"
      asideClassName="flex-[1_1_400px]"
      aside={
        <figure aria-labelledby={chartTitleId} className="m-0 flex min-w-0 flex-col gap-3.5">
          <StageChartHeading
            title="Answers by week"
            titleId={chartTitleId}
            legend={LEGEND}
            swatch="square"
          />
          <PulseWeeklyBars cycles={cycles} />
          <figcaption className="flex gap-2 text-xs leading-relaxed text-white/60">
            <Info aria-hidden="true" className="mt-[3px] size-3.5 flex-none" />
            <span>The number above a bar is the OK share among people who answered.</span>
          </figcaption>
        </figure>
      }
    >
      <StageHeadline
        id={headingId}
        value={formatPercent(summary.averagePercent)}
        caption="average score for the period"
      />
      <StageLead>
        {latest.isOpen
          ? `This week, ${latest.ok} of ${latest.ok + latest.notOk} answered OK and ${latest.notOk} answered Not OK.`
          : `In the ${formatShortDate(latest.startsOn)} cycle, ${latest.ok} of ${latest.ok + latest.notOk} answered OK and ${latest.notOk} answered Not OK.`}
      </StageLead>
      <StageFacts
        items={[
          { key: "minimum", label: "Minimum", value: formatPercent(summary.minPercent) },
          { key: "maximum", label: "Maximum", value: formatPercent(summary.maxPercent) },
          {
            key: "completion",
            label: "Completion",
            value: formatPercent((summary.answered / summary.expected) * 100),
            detail: `${summary.answered} of ${summary.expected}`,
          },
          {
            key: "latest-cycle",
            label: "Latest cycle",
            value: formatPercent(((latest.ok + latest.notOk) / latest.expected) * 100),
            detail: `${latest.ok + latest.notOk} of ${latest.expected}`,
          },
        ]}
      />
      <StageMeta
        items={[
          `Last Pulse ${formatShortDate(latest.startsOn)}`,
          `Next Pulse ${schedule.nextPulse}`,
          schedule.rhythm,
        ]}
      />
    </Stage>
  );
}

/**
 * The product fits one bar per 36px and drops the oldest; here the container hides them:
 * all eight from 288px, seven from 252px, six below that.
 */
const NARROW_HIDE: Record<number, string> = {
  7: "@max-[288px]:hidden",
  6: "@max-[252px]:hidden",
};

/** A bar that becomes the first one shown names its month even when the month did not change. */
const NARROW_FIRST_MONTH: Record<number, string> = {
  6: "hidden @max-[288px]:inline",
  5: "hidden @max-[252px]:inline",
};

function PulseWeeklyBars({ cycles }: { cycles: readonly PulseStageCycle[] }) {
  return (
    // Narrow plots show day numbers and name the month only where it changes, as the product does below 46px a bar.
    <div className="@container min-w-0">
      <ol className="m-0 flex list-none items-end p-0">
        {cycles.map((cycle, index) => {
          const previous = cycles[index - 1];
          const fromEnd = cycles.length - 1 - index;
          const month = formatShortDate(cycle.startsOn).split(" ")[0];
          const monthChanged =
            !previous || formatShortDate(previous.startsOn).split(" ")[0] !== month;

          return (
            <PulseBar
              key={cycle.startsOn}
              cycle={cycle}
              latest={index === cycles.length - 1}
              month={month}
              monthClassName={monthChanged ? "" : (NARROW_FIRST_MONTH[fromEnd] ?? "hidden")}
              className={NARROW_HIDE[fromEnd]}
              tooltipAlign={
                index < cycles.length / 3
                  ? "start"
                  : index >= (cycles.length * 2) / 3
                    ? "end"
                    : "center"
              }
            />
          );
        })}
      </ol>
    </div>
  );
}

function PulseBar({
  cycle,
  latest,
  month,
  monthClassName,
  className,
  tooltipAlign,
}: {
  cycle: PulseStageCycle;
  latest: boolean;
  /** Shown under the day number on narrow plots; `monthClassName` decides where. */
  month: string;
  monthClassName: string;
  className?: string;
  tooltipAlign: "start" | "center" | "end";
}) {
  const parts = pulseShareParts(cycle);
  const okHeight = Math.round(parts.ok * PLOT_HEIGHT);
  const date = formatShortDate(cycle.startsOn);
  const day = date.split(" ")[1];
  const description = cycle.isOpen
    ? `${date}, still open: ${parts.answered} of ${parts.expected} answered so far — ${cycle.ok} OK, ${cycle.notOk} Not OK.`
    : `${date}: ${parts.answered} of ${parts.expected} answered — ${cycle.ok} OK, ${cycle.notOk} Not OK.`;

  return (
    <li className={cn("group relative flex min-w-0 flex-1 flex-col items-center", className)}>
      <span className="sr-only">{description}</span>
      <div aria-hidden="true" className="flex w-full flex-col items-center">
        <div
          className="flex w-full flex-col items-center justify-end"
          style={{ height: PLOT_HEIGHT + 22 }}
        >
          {parts.answered > 0 ? (
            <>
              <span
                className={cn(
                  "mb-1.5 text-[11px] leading-none tabular-nums",
                  latest ? "font-semibold text-white" : "text-white/70",
                )}
              >
                {formatPercent(pulseOkPercent(cycle))}
              </span>
              <div className="flex w-[62%] max-w-9 min-w-3 flex-col" style={{ height: PLOT_HEIGHT }}>
                {parts.notOk > 0 ? (
                  <div
                    className="rounded-t-[4px] bg-[rgba(229,102,74,0.32)]"
                    style={{
                      height: Math.max(1, PLOT_HEIGHT - okHeight - (parts.ok > 0 ? PART_GAP : 0)),
                      marginBottom: parts.ok > 0 ? PART_GAP : 0,
                    }}
                  />
                ) : null}
                {parts.ok > 0 ? (
                  <div
                    className={cn("bg-[#28A68E]", parts.notOk > 0 ? null : "rounded-t-[4px]")}
                    style={{ height: okHeight }}
                  />
                ) : null}
              </div>
            </>
          ) : (
            <div
              className="w-[62%] max-w-9 min-w-3 rounded-[4px] border border-dashed border-white/20"
              style={{ height: PLOT_HEIGHT }}
            />
          )}
        </div>
        <div className="h-px w-full bg-white/15" />
        <div
          className={cn(
            "mt-2 flex min-h-[2.4em] flex-col items-center text-center text-[11px] leading-tight whitespace-nowrap tabular-nums",
            latest ? "font-semibold text-white" : "text-white/55",
          )}
        >
          <span className="@min-[368px]:hidden">{day}</span>
          <span
            className={cn("text-[10px] font-normal text-white/60 @min-[368px]:hidden", monthClassName)}
          >
            {month}
          </span>
          <span className="hidden @min-[368px]:inline">{date}</span>
          {cycle.isOpen ? (
            <span className="text-[10px] font-normal text-white/60">ongoing</span>
          ) : null}
        </div>
      </div>
      <div
        aria-hidden="true"
        className={cn(
          "pointer-events-none absolute bottom-[calc(100%+4px)] z-10 hidden w-max max-w-[14rem] rounded-lg border border-white/10 bg-[#1d1d22] px-2.5 py-1.5 text-xs leading-snug whitespace-normal text-white/85 shadow-[0_8px_24px_rgba(0,0,0,0.45)] group-hover:block",
          tooltipAlign === "start"
            ? "left-0"
            : tooltipAlign === "end"
              ? "right-0"
              : "left-1/2 -translate-x-1/2",
        )}
      >
        {description}
      </div>
    </li>
  );
}
